import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { ArrowLeft, Brain, Mail, Bot, FileText, RefreshCw } from 'lucide-react';
import InnovationChallenge2024 from './projects/InnovationChallenge2024';
import InnovationChallenge2025 from './projects/InnovationChallenge2025';

const AITimeline = () => {
  const { setCurrentPage } = useAppStore();
  const [selectedYear, setSelectedYear] = useState(null);

  const challenges = [
    { 
      year: 2024, 
      title: 'rule master',
      image: '/pic/innovation challenge/Innovation Challenge 2024.webp',
      position: { x: '30%', y: '50%' },
      rotation: -5,
      projects: [
        { id: 'rag', name: 'RAG Research', image: '/placeholder-rag.jpg' },
        { id: 'graphrag', name: 'GraphRAG Exploration', image: '/placeholder-graphrag.jpg' },
        { id: 'ai-workflow', name: 'AI Workflow', image: '/placeholder-workflow.jpg' }
      ]
    },
    {
      year: 2025,
      title: 'Five Projects',
      image: '/pic/innovation challenge/Innovation Challenge 2025.webp',
      position: { x: '70%', y: '50%' },
      rotation: 3,
      projects: [
        {
          id: 'bsd-cli',
          name: 'BSD CLI',
          icon: Brain,
          color: 'from-blue-500 to-cyan-500',
          versions: [
            { version: 'v1.0', description: 'Basic Document Parsing', image: '/placeholder-bsd-v1.jpg' },
            { version: 'v2.0', description: 'Smart Analysis Framework', image: '/placeholder-bsd-v2.jpg' },
            { version: 'v3.0', description: 'Six-Dimensional Analysis System', image: '/placeholder-bsd-v3.jpg' }
          ]
        },
        {
          id: 'email-assistant',
          name: 'Email Assistant',
          icon: Mail,
          color: 'from-green-500 to-teal-500',
          versions: [
            { version: 'Initial', description: 'Outlook Plugin', image: '/placeholder-email-v1.jpg' },
            { version: 'Enhanced', description: 'AI Agent Integration', image: '/placeholder-email-v2.jpg' },
            { version: 'Complete', description: 'Multimodal Processing', image: '/placeholder-email-v3.jpg' }
          ]
        },
        {
          id: 'transformation-robot',
          name: 'Product Transformation Robot',
          icon: Bot,
          color: 'from-purple-500 to-pink-500',
          versions: [
            { version: 'App', description: 'Mobile Prototype', image: '/placeholder-ptr-app.jpg' },
            { version: 'Migrated', description: 'Web Adaptation', image: '/placeholder-ptr-web1.jpg' },
            { version: 'Web', description: 'Complete Web Application', image: '/placeholder-ptr-web2.jpg' }
          ]
        },
        {
          id: 'rfp-assistant',
          name: 'RFP Assistant',
          icon: FileText,
          color: 'from-orange-500 to-red-500',
          versions: [
            { version: 'App', description: 'Desktop Application', image: '/placeholder-rfp-app.jpg' },
            { version: 'Web', description: 'Web Interface', image: '/placeholder-rfp-web1.jpg' },
            { version: 'Complete', description: 'Full Functionality', image: '/placeholder-rfp-web2.jpg' }
          ]
        },
        {
          id: 'tech-refresh',
          name: 'Tech Refresh',
          icon: RefreshCw,
          color: 'from-yellow-500 to-amber-500',
          description: 'Infrastructure Modernization Initiative'
        }
      ]
    }
  ];

  const ChallengeCard = ({ challenge }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0, rotate: 0 }}
      animate={{ opacity: 1, scale: 1, rotate: challenge.rotation }}
      transition={{ duration: 0.8, type: "spring" }}
      className="absolute cursor-pointer"
      style={{ 
        left: challenge.position.x, 
        top: challenge.position.y
      }}
      onClick={() => setSelectedYear(challenge.year)}
      whileHover={{ scale: 1.05, rotate: 0 }}
    >
      <div className="relative" style={{ transform: 'translate(-50%, -50%)' }}>
        {/* Irregular Border Effect */}
        <div className="absolute -inset-4 bg-white rounded-lg" style={{
          transform: `rotate(${challenge.rotation * 0.5}deg)`,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}></div>

        {/* Photo */}
        <div className="relative bg-white p-3 rounded-lg shadow-2xl" style={{ width: '500px', height: '400px' }}>
          <img 
            src={challenge.image} 
            alt={`${challenge.year} Innovation Challenge`}
            className="w-full h-full object-cover rounded"
          />
          
          {/* Year Label */}
          <div className="absolute -bottom-3 -right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full font-bold text-xl shadow-lg transform rotate-12">
            {challenge.year}
          </div>
        </div>

        {/* Tape Effect */}
        <div className="absolute -top-2 left-1/4 w-20 h-8 bg-yellow-100/60 transform -rotate-12" style={{
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
        }}></div>
      </div>
    </motion.div>
  );

  return (
    <div className="page">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-8 left-8 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
        onClick={() => setCurrentPage('landing')}
      >
        <ArrowLeft className="w-6 h-6" />
      </motion.button>

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-8 left-1/2.5 transform -translate-x-1 text-center"
      >
        <h1 className="text-4xl font-bold mb-2">AI Innovation</h1>
        <p className="text-gray-300">Click year to view details</p>
      </motion.div>

      {/* Innovation Challenge Cards */}
      <div className="relative w-full h-full">
        {challenges.map((challenge) => (
          <ChallengeCard key={challenge.year} challenge={challenge} />
        ))}
      </div>

      {/* Connection Line */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} preserveAspectRatio="none" viewBox="0 0 100 100">
        <motion.line
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, delay: 0.8 }}
          x1={30}
          y1={50}
          x2={70}
          y2={50}
          stroke="rgba(78, 205, 196, 0.3)"
          strokeWidth="0.2"
          strokeDasharray="2,2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedYear === 2024 && (
          <InnovationChallenge2024 onClose={() => setSelectedYear(null)} />
        )}
        {selectedYear === 2025 && (
          <InnovationChallenge2025 onClose={() => setSelectedYear(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AITimeline;
